/**
 * Paracha Constructions — Animations v2
 * ─────────────────────────────────────
 * • Lenis smooth scroll (desktop only, disabled on phone/tablet ≤768px)
 * • GSAP + ScrollTrigger data-attribute driven: add  data-anim  to any
 *   element in any section and it will animate in on scroll.
 * • Section-level fallback: any .shopify-section child that doesn't have
 *   a data-anim still fades in via a generic class scan.
 * • Typewriter hook preserved.
 * • Lazy-load images.
 */

(function () {
  'use strict';

  if (window.__parachaAnimV2) return;
  window.__parachaAnimV2 = true;

  /* ────────────────────────────────────────────────────────────────
     UTILITIES
  ──────────────────────────────────────────────────────────────── */
  const isMobile = () => window.innerWidth <= 768 ||
    (/Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth <= 820);

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ────────────────────────────────────────────────────────────────
     SCRIPT LOADER
  ──────────────────────────────────────────────────────────────── */
  function loadScript(src) {
    return new Promise((res, rej) => {
      if (document.querySelector(`script[src="${src}"][data-loaded]`)) { res(); return; }
      const s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = () => { s.dataset.loaded = '1'; res(); };
      s.onerror = rej;
      document.head.appendChild(s);
    });
  }

  async function loadDeps() {
    if (!window.gsap) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js');
    if (!window.ScrollTrigger) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js');
    if (!window.Lenis) await loadScript('https://unpkg.com/lenis@1.1.18/dist/lenis.min.js');
  }

  /* ────────────────────────────────────────────────────────────────
     LENIS SMOOTH SCROLL (desktop only)
  ──────────────────────────────────────────────────────────────── */
  let lenis = null;

  function initLenis() {
    if (isMobile()) {
      if (lenis) { lenis.destroy(); lenis = null; }
      document.documentElement.classList.remove('lenis-smooth');
      return;
    }
    if (lenis || !window.Lenis) return;

    lenis = new window.Lenis({
      duration: 1.2,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 0.95,
      touchMultiplier: 1.5
    });

    window.__lenis = lenis;
    document.documentElement.classList.add('lenis-smooth');

    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(t => lenis && lenis.raf(t * 1000));
      window.gsap.ticker.lagSmoothing(0);
    } else {
      (function raf(t) { lenis && (lenis.raf(t), requestAnimationFrame(raf)); })(0);
    }
  }

  /* ────────────────────────────────────────────────────────────────
     CSS — inject initial hidden states for animated elements
     We use CSS classes rather than inline style so elements are
     invisible until JS runs (no layout shift visible on load).
  ──────────────────────────────────────────────────────────────── */
  function injectAnimationCSS() {
    if (document.getElementById('ph-anim-styles')) return;
    const style = document.createElement('style');
    style.id = 'ph-anim-styles';
    style.textContent = `
      [data-anim] {
        opacity: 0;
        will-change: transform, opacity;
      }
      [data-anim="fade-up"]    { transform: translateY(40px); }
      [data-anim="fade-down"]  { transform: translateY(-30px); }
      [data-anim="fade-left"]  { transform: translateX(50px); }
      [data-anim="fade-right"] { transform: translateX(-50px); }
      [data-anim="scale-in"]   { transform: scale(0.88); }
      [data-anim="zoom-in"]    { transform: scale(0.75); }
      [data-anim="flip-up"]    { transform: perspective(600px) rotateX(20deg) translateY(30px); }

      /* When JS fires: GSAP will set opacity:1 and transform:none */
      [data-anim-done] {
        opacity: 1 !important;
        transform: none !important;
      }

      /* Stagger children helper */
      [data-anim-children] > * {
        opacity: 0;
        transform: translateY(35px);
        will-change: transform, opacity;
      }
    `;
    document.head.appendChild(style);
  }

  /* ────────────────────────────────────────────────────────────────
     CORE: animate [data-anim] elements
  ──────────────────────────────────────────────────────────────── */
  function buildFromProps(attr) {
    const map = {
      'fade-up':    { opacity: 0, y: 40 },
      'fade-down':  { opacity: 0, y: -30 },
      'fade-left':  { opacity: 0, x: 50 },
      'fade-right': { opacity: 0, x: -50 },
      'scale-in':   { opacity: 0, scale: 0.88 },
      'zoom-in':    { opacity: 0, scale: 0.75 },
      'flip-up':    { opacity: 0, y: 30, rotationX: 20 }
    };
    return map[attr] || { opacity: 0, y: 35 };
  }

  function animateDataAttrElements() {
    if (!window.gsap || !window.ScrollTrigger) return;
    const { gsap, ScrollTrigger } = window;

    /* --- Single [data-anim] elements --- */
    document.querySelectorAll('[data-anim]:not([data-anim-done])').forEach(el => {
      el.setAttribute('data-anim-done', '');
      const from = buildFromProps(el.dataset.anim);
      const delay = parseFloat(el.dataset.animDelay || 0);
      const duration = parseFloat(el.dataset.animDuration || 0.85);

      gsap.fromTo(el,
        { ...from },
        {
          opacity: 1, y: 0, x: 0, scale: 1, rotationX: 0,
          duration,
          delay,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 90%',
            once: true
          }
        }
      );
    });

    /* --- Stagger children [data-anim-children] --- */
    document.querySelectorAll('[data-anim-children]:not([data-anim-done])').forEach(parent => {
      parent.setAttribute('data-anim-done', '');
      const children = Array.from(parent.children);
      if (!children.length) return;
      const stagger = parseFloat(parent.dataset.stagger || 0.12);

      gsap.fromTo(children,
        { opacity: 0, y: 35 },
        {
          opacity: 1, y: 0,
          duration: 0.8,
          stagger,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: parent,
            start: 'top 88%',
            once: true
          }
        }
      );
    });
  }

  /* ────────────────────────────────────────────────────────────────
     SECTION-LEVEL AUTO ANIMATIONS
     For sections that don't have data-anim yet — we auto-tag key
     elements across all known Paracha section CSS classes.
  ──────────────────────────────────────────────────────────────── */
  const AUTO_SELECTORS = [
    /* Headers & eyebrows */
    '.ph-hero__eyebrow',
    '.ph-hero__heading',
    '.ph-hero__subtext',
    '.ph-hero__actions',
    '.ph-hero__features',
    '.ph-hero__video-card',

    /* General hero */
    '.ph-gh__eyebrow',
    '.ph-gh__heading',
    '.ph-gh__subtext',

    /* About */
    '.ph-about-header',
    '.ph-about__body',
    '.ph-about__credentials',
    '.ph-about__signature',
    '.ph-about__media',

    /* Our Services */
    '.ph-services__header',
    '.ph-services__card',

    /* Services Page Grid */
    '.ph-sp__header',
    '.ph-sp__grid-card',

    /* Single Services Details */
    '.ph-ssd__header',
    '.ph-ssd__content-col',
    '.ph-ssd__media-col',

    /* Portfolio */
    '.ph-pm__header',
    '.ph-pm__filters',
    '.ph-pm__card',

    /* Recent Projects */
    '.ph-projects__header',
    '.ph-projects__card',

    /* Before & After */
    '.ph-before-after__header',
    '.ph-before-after__slider',

    /* Facility & Ops */
    '.ph-facility__header',
    '.ph-facility__content',
    '.ph-facility__feature',

    /* Animated numbers */
    '.ph-stats__item',

    /* Testimonials */
    '.ph-testimonials__header',
    '.ph-test__card',
    '.ph-testimonials__card',

    /* Contact */
    '.ph-ct__header',
    '.ph-ct__pillar-card',
    '.ph-ct__form-container',
    '.ph-ct__info-container',

    /* Footer */
    '.ph-footer__col',
    '.ph-footer__bottom',
  ];

  function autoTagElements() {
    AUTO_SELECTORS.forEach(selector => {
      document.querySelectorAll(selector).forEach((el, i) => {
        if (el.hasAttribute('data-anim') || el.hasAttribute('data-anim-done')) return;

        // Pick animation direction based on element type
        let anim = 'fade-up';
        if (selector.includes('media') || selector.includes('img')) anim = 'scale-in';
        if (selector.includes('eyebrow')) anim = 'fade-down';

        el.setAttribute('data-anim', anim);
        // Stagger siblings by index
        const baseDelay = parseFloat(el.dataset.animDelay || 0);
        el.dataset.animDelay = (baseDelay + i * 0.07).toFixed(2);
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────
     SECTION HEADER TEXT SPLITS (per-word reveal)
  ──────────────────────────────────────────────────────────────── */
  const HEADLINE_SELECTORS = [
    '.ph-services__header h2',
    '.ph-sp__header h2',
    '.ph-ssd__header h2',
    '.ph-pm__header h2',
    '.ph-ct__header h2',
    '.ph-about-header h2',
    '.ph-projects__header h2',
    '.ph-testimonials__header h2',
    '.ph-before-after__header h2',
    '.ph-facility__header h2',
    '.ph-gh__heading',
    '.ph-footer__col h3'
  ];

  function splitAndAnimateHeadings() {
    if (!window.gsap || !window.ScrollTrigger) return;
    const { gsap, ScrollTrigger } = window;

    HEADLINE_SELECTORS.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        if (el.hasAttribute('data-split-done')) return;
        el.setAttribute('data-split-done', '');

        // Remove data-anim so we don't double animate
        el.removeAttribute('data-anim');
        el.removeAttribute('data-anim-done');
        el.style.opacity = '';
        el.style.transform = '';

        const text = el.textContent.trim();
        const words = text.split(/\s+/);
        el.innerHTML = words
          .map(w => `<span class="ph-word-wrap" style="display:inline-block;overflow:hidden;vertical-align:bottom">
            <span class="ph-word" style="display:inline-block">${w}</span>
          </span>`)
          .join(' ');

        const wordSpans = el.querySelectorAll('.ph-word');

        gsap.fromTo(wordSpans,
          { y: '100%', opacity: 0 },
          {
            y: '0%',
            opacity: 1,
            duration: 0.7,
            stagger: 0.08,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 90%',
              once: true
            }
          }
        );
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────
     LAZY LOAD IMAGES
  ──────────────────────────────────────────────────────────────── */
  function lazyLoadImages() {
    document.querySelectorAll('img:not([loading])').forEach(img => {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
    });

    // Fade in images as they load
    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      if (img.complete) return;
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.5s ease';
      img.addEventListener('load', () => { img.style.opacity = '1'; }, { once: true });
    });
  }

  /* ────────────────────────────────────────────────────────────────
     MAIN INIT
  ──────────────────────────────────────────────────────────────── */
  async function init() {
    lazyLoadImages();
    injectAnimationCSS();

    await loadDeps();

    if (!window.gsap || !window.ScrollTrigger) {
      console.warn('[Paracha Anim] GSAP/ScrollTrigger not available.');
      return;
    }

    window.gsap.registerPlugin(window.ScrollTrigger);

    // Adjust ScrollTrigger scroller if Lenis is active
    initLenis();

    if (prefersReduced) return; // Respect accessibility

    // Auto-tag elements that don't have data-anim yet
    autoTagElements();

    // Per-word heading reveals
    splitAndAnimateHeadings();

    // Animate everything with data-anim
    animateDataAttrElements();

    // Refresh to account for images/fonts loading
    setTimeout(() => {
      window.ScrollTrigger.refresh();
    }, 500);
  }

  /* ────────────────────────────────────────────────────────────────
     EVENT HOOKS
  ──────────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Customizer live reload
  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => {
      lazyLoadImages();
      autoTagElements();
      animateDataAttrElements();
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }, 200);
  });

  // Resize handler: re-evaluate Lenis phone/desktop state
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      initLenis();
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }, 250);
  });

})();
